import { Component, OnDestroy, OnInit, ViewChild, AfterViewInit, inject, DOCUMENT, effect } from '@angular/core';

import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { SharedService } from './_services/shared.service';
import { ThemeHighlightService } from './_services/theme-highlight.service';
import { NgcCookieConsentService, NgcInitializationErrorEvent, NgcInitializingEvent, NgcStatusChangeEvent} from 'ngx-cookieconsent';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { ResponsiveMode } from './enum/responsiveModes';
import { BreakpointObserver, Breakpoints, LayoutModule } from '@angular/cdk/layout';
import { CookieService } from 'ngx-cookie-service';
import { verticalOverrideHTML, verticalDarkOverrideHTML, verticalCustomOverrideHTML,
        horizontalOverrideHTML, horizontalDarkOverrideHTML, horizontalCustomOverrideHTML} from './app.config';
import { ThemeService } from './_services/theme.service';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterOutlet, TranslateModule, LayoutModule],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {

  private themeHighlight = inject(ThemeHighlightService);
  private document = inject(DOCUMENT);

  title = 'client';
  //keep refs to subscriptions to be able to unsubscribe later
  private popupOpenSubscription!: Subscription;
  private popupCloseSubscription!: Subscription;
  private initializingSubscription!: Subscription;
  private initializedSubscription!: Subscription;
  private initializationErrorSubscription!: Subscription;
  private statusChangeSubscription!: Subscription;
  private revokeChoiceSubscription!: Subscription;
  private noCookieLawSubscription!: Subscription;

  modalActive: boolean = false;
  horizontalView: boolean = true;
  vertialView: boolean = false;
  viewMode: string = ResponsiveMode.horizontal;
  responsiveMode = ResponsiveMode;



  constructor(public sharedService: SharedService,
              private ccService: NgcCookieConsentService,
              private responsive: BreakpointObserver,
              private router: Router,
              private route: ActivatedRoute,
              private cookieService: CookieService,
              public themeService: ThemeService
              // private translateService: TranslateService
  ){
    // Set up theme variable highlighting effect
    effect(() => {
      const varName = this.themeHighlight.highlightedVariable();

      // Remove highlight from all previously highlighted elements
      const previouslyHighlighted = this.document.querySelectorAll('.theme-highlighted');
      previouslyHighlighted.forEach(el => el.classList.remove('theme-highlighted'));

      // If we have a variable to highlight, apply the class to matching elements
      if (varName) {
        requestAnimationFrame(() => {
          // Find elements where data-theme-key contains the variable (supports multiple keys)
          const elements = Array.from(this.document.querySelectorAll('[data-theme-key]'))
            .filter(el => {
              const themeKeys = el.getAttribute('data-theme-key') || '';
              return themeKeys.split(/[\s,]+/).includes(varName);
            });
          elements.forEach(el => el.classList.add('theme-highlighted'));
        });
      }
    });
  }
  ngOnInit(): void {
    this.getCloudflareCookie();
    // this.checkCookieConsent();
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy() {
    // unsubscribe to cookieconsent observables to prevent memory leaks
    this.popupOpenSubscription.unsubscribe();
    this.popupCloseSubscription.unsubscribe();
    this.initializingSubscription.unsubscribe();
    this.initializedSubscription.unsubscribe();
    this.initializationErrorSubscription.unsubscribe();
    this.statusChangeSubscription.unsubscribe();
    this.revokeChoiceSubscription.unsubscribe();
    this.noCookieLawSubscription.unsubscribe();
  }

  getCloudflareCookie() {
    const cfBmCookie = this.cookieService.get('__cf_bm');
    const allCookies = this.cookieService.getAll();
  }

  private checkCookieConsent(): void {

    this.responsive.observe([
      Breakpoints.HandsetPortrait,
    ])
      .subscribe(result => {
        const breakpoints = result.breakpoints;

        if(breakpoints[Breakpoints.HandsetPortrait]){
          if(!this.themeService.isDarkMode()){
            this.reinitializeCookieConsent(verticalOverrideHTML);
          } else if(this.themeService.isDarkMode()){
            this.reinitializeCookieConsent(verticalDarkOverrideHTML);
          } else if(this.themeService.isDarkMode()){
            this.reinitializeCookieConsent(verticalCustomOverrideHTML);
          }

          this.viewMode = ResponsiveMode.vertical;
          this.vertialView = true;
          this.horizontalView = false;
        }
        else {
          this.reinitializeCookieConsent(horizontalOverrideHTML);
          this.viewMode = ResponsiveMode.horizontal;
          this.vertialView = false;
          this.horizontalView = true;
        }
      });

      this.themeService.darkMode$.subscribe((isDarkTheme) => {
        if(isDarkTheme){
          if(this.horizontalView){
            this.reinitializeCookieConsent(horizontalDarkOverrideHTML);
          } else {
            this.reinitializeCookieConsent(verticalDarkOverrideHTML);
          }
        } else {
          if(this.horizontalView){
            this.reinitializeCookieConsent(horizontalOverrideHTML);
          } else {
            this.reinitializeCookieConsent(verticalOverrideHTML);
          }
        }
      });


    // subscribe to cookieconsent observables to react to main events
    this.popupOpenSubscription = this.ccService.popupOpen$.subscribe(
    () => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.popupCloseSubscription = this.ccService.popupClose$.subscribe(
    () => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.initializingSubscription = this.ccService.initializing$.subscribe(
    (event: NgcInitializingEvent) => {
      // the cookieconsent is initilializing... Not yet safe to call methods like `NgcCookieConsentService.hasAnswered()`
      console.log(`initializing: ${JSON.stringify(event)}`);
    });

    this.initializedSubscription = this.ccService.initialized$.subscribe(
    () => {
      // the cookieconsent has been successfully initialized.
      // It's now safe to use methods on NgcCookieConsentService that require it, like `hasAnswered()` for eg...
      console.log(`initialized: ${JSON.stringify(event)}`);
    });

    this.initializationErrorSubscription = this.ccService.initializationError$.subscribe(
    (event: NgcInitializationErrorEvent) => {

      // the cookieconsent has failed to initialize...
      console.log(`initializationError: ${JSON.stringify(event.error?.message)}`);
    });

    this.statusChangeSubscription = this.ccService.statusChange$.subscribe(
    (event: NgcStatusChangeEvent) => {
      if (event.status === 'deny') {
        // User clicked the decline button.
        // Create and save a cookie that indicates cookies were declined.
        // The third argument is options (here we set expiration to 365 days and path to '/').
        this.cookieService.set('cookieConsentLuxePricing', 'declined', { expires: 365, path: '/' });
      }
      if (event.status === 'allow') {
        // User clicked the decline button.
        // Create and save a cookie that indicates cookies were declined.
        // The third argument is options (here we set expiration to 365 days and path to '/').
        this.cookieService.set('cookieConsentLuxePricing', 'accepted', { expires: 365, path: '/' });
      }


    });

    this.revokeChoiceSubscription = this.ccService.revokeChoice$.subscribe(
    () => {

      // you can use this.ccService.getConfig() to do stuff...
    });


    // this.translateService.addLangs(['en']); // , 'fr'
    // this.translateService.setDefaultLang('en');
    // this.translateService.use('en'); // Set the initial language

    // // translate example use
    // // <p>{{ 'hello' | translate }}</p>
    // // this.translate.use('fr'); // Switch to French

    // const browserLang = this.translateService.getBrowserLang() || 'en';
    // this.translateService.use(browserLang.match(/en|fr/) ? browserLang : 'en');

    // this.translateService//
    //   .get(['cookie.header', 'cookie.message', 'cookie.dismiss', 'cookie.allow', 'cookie.deny', 'cookie.link', 'cookie.policy'])
    //   .subscribe(data => {

    //     this.ccService.getConfig().content = this.ccService.getConfig().content || {} ;
    //     // Override default messages with the translated ones
    //     this.ccService.getConfig().content!.header = data['cookie.header'];
    //     this.ccService.getConfig().content!.message = data['cookie.message'];
    //     this.ccService.getConfig().content!.dismiss = data['cookie.dismiss'];
    //     this.ccService.getConfig().content!.allow = data['cookie.allow'];
    //     this.ccService.getConfig().content!.deny = data['cookie.deny'];
    //     this.ccService.getConfig().content!.link = environment.guestOfferDomain + 'privacyAndTerms';
    //     this.ccService.getConfig().content!.policy = data['cookie.policy'];

    //     this.ccService.destroy(); // remove previous cookie bar (with default messages)
    //     this.ccService.init(this.ccService.getConfig()); // update config with translated messages
    //   });


  }


  private reinitializeCookieConsent(overrideHTML: string): void {

    const hasConsented = this.cookieService.check('cookieConsentLuxePricing');
    this.ccService.destroy();
    this.ccService.init({
      ...this.ccService.getConfig(),
      overrideHTML: overrideHTML,
      autoOpen: !hasConsented
    });
  }

}

